import type { ThirdPartyResponsesRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetThirdPartyResponsesSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetThirdPartyResponsesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

/**
 * Responses reach `awaiting_review` through an EXTERNAL actor (a respondent
 * submitting in the portal), so no client-side invalidation can observe it and
 * the V3 stack has no subscription transport — the feed is POLLED.
 *
 * 30s, not the 5s used by the focused feeds: this is an org-wide list a reviewer
 * leaves open as a landing page, and it re-fetches every row each tick. The
 * subscription it replaces was live, so polling off entirely would silently drop
 * that property; 30s keeps it at a fraction of the cost. The reviewer's own
 * status changes do not rely on this interval — they invalidate the key
 * directly (see `useUpdateThirdPartyResponseStatus`).
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Holding-pattern subscription hook for the third-party-responses register
 * (COI-581).
 *
 * - `trpc` ON  → polls `frontend.thirdPartyResponse.register`.
 * - `trpc` OFF → the legacy Hasura `getThirdPartyResponses` subscription.
 *
 * Built on `createSubscriptionHook` so the poll → real tRPC subscription swap
 * (RSP-4320) is a one-place change.
 */
export const useGetThirdPartyResponsesSubscription = createSubscriptionHook<
  undefined,
  ThirdPartyResponsesRegisterResponseRow[],
  GetThirdPartyResponsesSubscription
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.thirdPartyResponse.register.queryOptions(),
  trpcRefetchInterval: () => POLL_INTERVAL_MS,
  mapTrpcDataToGraphQL: (rows) => ({
    third_party_response: rows,
  }),
  graphqlDocument: GetThirdPartyResponsesDocument,
});
